import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Truck } from "lucide-react";
import { DeleteButton } from "./DeleteButton";
import { EasyshipPanel } from "./EasyshipPanel";
import { field, label } from "./ProductEditors";
import { money } from "@/components/landing/useProducts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const blank = { label: "", description: "", amount: "", scope: "all", min_days: 7, max_days: 12, free_over: "", active: true, sort: 1 };
const SCOPE = { all: "Worldwide", us: "United States only", intl: "International only" };

const toForm = (r) => ({ ...r, amount: (r.amount_cents / 100).toString(), free_over: r.free_over_cents == null ? "" : (r.free_over_cents / 100).toString() });
const toPayload = (f) => ({
  label: f.label.trim(), description: f.description.trim(), scope: f.scope, active: f.active, sort: Number(f.sort) || 0,
  amount_cents: Math.round(parseFloat(f.amount || "0") * 100), min_days: Number(f.min_days), max_days: Number(f.max_days),
  free_over_cents: f.free_over === "" ? null : Math.round(parseFloat(f.free_over) * 100),
});

export const ShippingTab = ({ headers }) => {
  const [rates, setRates] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => axios.get(`${API}/admin/shipping`, { headers }).then((r) => setRates(r.data)).catch(() => toast.error("Could not load delivery rates."));
  useEffect(() => { load(); }, [headers]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = toPayload(editing);
      if (editing.id) await axios.put(`${API}/admin/shipping/${editing.id}`, body, { headers });
      else await axios.post(`${API}/admin/shipping`, body, { headers });
      toast.success("Delivery option saved");
      setEditing(null);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save. Check the fields.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    await axios.delete(`${API}/admin/shipping/${r.id}`, { headers });
    toast.success("Delivery option deleted");
    load();
  };

  const toggle = async (r) => {
    await axios.put(`${API}/admin/shipping/${r.id}`, { ...toPayload(toForm(r)), active: !r.active }, { headers });
    load();
  };

  if (rates === null) return <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-[#5f5e5a]" /></div>;
  const set = (patch) => setEditing((s) => ({ ...s, ...patch }));

  return (
    <div data-testid="admin-shipping" className="mt-8">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <p className="font-mono-d text-[11px] uppercase tracking-[0.2em] text-[#5f5e5a]">{rates.length} delivery option{rates.length === 1 ? "" : "s"} · customers pick one in the bag · charged by Stripe</p>
        <button data-testid="shipping-add" onClick={() => setEditing({ ...blank })} className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#0a0a0b] text-white font-mono-d text-[10px] uppercase tracking-[0.2em] hover:bg-[#ff2a85] transition-colors"><Plus size={14} /> Add option</button>
      </div>

      {editing && (
        <form data-testid="shipping-form" onSubmit={save} className="mb-6 rounded-3xl border border-[#e3e2dc] bg-[#faf9f6] p-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div><span className={label}>Name</span><input data-testid="sf-label" className={field} required value={editing.label} onChange={(e) => set({ label: e.target.value })} placeholder="Standard" /></div>
          <div><span className={label}>Description</span><input data-testid="sf-description" className={field} value={editing.description} onChange={(e) => set({ description: e.target.value })} placeholder="Tracked · 7-12 business days" /></div>
          <div><span className={label}>Price (USD)</span><input data-testid="sf-amount" className={field} required type="number" min="0" step="0.01" value={editing.amount} onChange={(e) => set({ amount: e.target.value })} placeholder="9.99" /></div>
          <div><span className={label}>Applies to</span>
            <select data-testid="sf-scope" className={field} value={editing.scope} onChange={(e) => set({ scope: e.target.value })}>{Object.entries(SCOPE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
          <div><span className={label}>Min business days</span><input data-testid="sf-min" className={field} type="number" min="1" value={editing.min_days} onChange={(e) => set({ min_days: e.target.value })} /></div>
          <div><span className={label}>Max business days</span><input data-testid="sf-max" className={field} type="number" min="1" value={editing.max_days} onChange={(e) => set({ max_days: e.target.value })} /></div>
          <div><span className={label}>Free over (USD, optional)</span><input data-testid="sf-free-over" className={field} type="number" min="0" step="0.01" value={editing.free_over} onChange={(e) => set({ free_over: e.target.value })} placeholder="e.g. 250" /></div>
          <div><span className={label}>Sort order</span><input data-testid="sf-sort" className={field} type="number" min="0" value={editing.sort} onChange={(e) => set({ sort: e.target.value })} /></div>
          <div className="sm:col-span-2 lg:col-span-4 flex items-center justify-between gap-3 pt-1">
            <label className="flex items-center gap-2 font-mono-d text-[10px] uppercase tracking-[0.2em] text-[#5f5e5a] cursor-pointer"><input data-testid="sf-active" type="checkbox" checked={editing.active} onChange={(e) => set({ active: e.target.checked })} className="accent-[#ff2a85] h-4 w-4" /> Active</label>
            <div className="flex gap-3">
              <button type="button" data-testid="sf-cancel" onClick={() => setEditing(null)} className="h-11 px-5 rounded-full border border-[#e3e2dc] font-mono-d text-[10px] uppercase tracking-[0.2em] hover:border-[#0a0a0b]">Cancel</button>
              <button type="submit" data-testid="sf-save" disabled={saving} className="h-11 px-6 rounded-full bg-[#ff2a85] hover:bg-[#e60067] text-white font-mono-d text-[10px] uppercase tracking-[0.2em] disabled:opacity-60 flex items-center gap-2">{saving && <Loader2 size={14} className="animate-spin" />} Save</button>
            </div>
          </div>
        </form>
      )}

      {!rates.length ? (
        <p data-testid="shipping-empty" className="mt-16 text-center text-[#5f5e5a] font-mono-d text-[11px] uppercase tracking-[0.2em]">No delivery options — customers can't check out until you add one.</p>
      ) : (
        <ul className="space-y-3">
          {rates.map((r) => (
            <li key={r.id} data-testid={`shipping-rate-${r.id}`} className={`border border-[#e3e2dc] rounded-2xl bg-[#faf9f6] p-5 flex flex-col sm:flex-row sm:items-center gap-4 ${r.active ? "" : "opacity-60"}`}>
              <span className="h-10 w-10 rounded-full bg-[#0a0a0b] text-white flex items-center justify-center shrink-0"><Truck size={16} /></span>
              <div className="flex-1 min-w-0">
                <p className="font-display font-extrabold uppercase text-sm">{r.label} <span className="font-mono-d text-[10px] tracking-[0.15em] text-[#ff2a85] ml-2">{money(r.amount_cents)}</span>{!r.active && <span className="ml-2 font-mono-d text-[9px] uppercase tracking-[0.15em] text-[#5f5e5a]">Inactive</span>}</p>
                <p className="text-xs text-[#5f5e5a] mt-1">{r.description || `${r.min_days}-${r.max_days} business days`} · {SCOPE[r.scope]}{r.free_over_cents != null ? ` · Free over ${money(r.free_over_cents)}` : ""}</p>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`shipping-toggle-${r.id}`} onClick={() => toggle(r)} className="h-9 px-3 rounded-full border border-[#e3e2dc] hover:border-[#0a0a0b] font-mono-d text-[9px] uppercase tracking-[0.2em]">{r.active ? "Disable" : "Enable"}</button>
                <button data-testid={`shipping-edit-${r.id}`} onClick={() => setEditing(toForm(r))} className="h-9 w-9 rounded-full border border-[#e3e2dc] hover:border-[#0a0a0b] flex items-center justify-center" aria-label="Edit"><Pencil size={13} /></button>
                <DeleteButton testId={`shipping-delete-${r.id}`} title={`Delete "${r.label}"?`} description="Shoppers will no longer see this delivery option at checkout. Existing orders keep their delivery details." onConfirm={() => remove(r)} />
              </div>
            </li>
          ))}
        </ul>
      )}
      <EasyshipPanel headers={headers} />
    </div>
  );
};
